import React, { FC, useRef } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ProjectId, TaskId } from 'api/models';
import { ProjectAPI } from 'api/project';
import { FieldOption, StatusGroup } from 'api/tasks';
import { Loading } from 'components/atoms/loading';
import { TaskList } from 'components/molecules/tasks/task-list';
import { useCurrentSpaceTab } from 'components/organisms/spaces/space-tab/context';
import { GENERAL_API_STALE } from 'config/query-config';
import { SquadContext } from 'contexts';
import { DEFAULT_SQUAD } from 'contexts/squad';
import { useStatusField } from 'data/fields/hooks/account-fields';
import {
  createTaskFromSpaceTabSagaAction,
  deleteTaskFromSpaceTabSagaAction,
} from 'data/space-tabs/actions';
import { Task } from 'data/tasks';
import { updateTaskSagaAction } from 'data/tasks/actions';
import { useTasksByIds } from 'data/tasks/hooks';
import { useCurrentProject } from 'hooks/use-current-project';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';

const LoadingWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
`;

const TaskListWrapper = styled.div`
  padding: var(--spacing-05);
`;

export const TasksTab: FC = () => {
  const { projectId } = useCurrentProject();
  const { spaceTabId, taskList } = useCurrentSpaceTab();
  const navigateToTask = useNavigateToTask();

  const tasks = useTasksByIds(ProjectId(projectId), taskList);

  const dispatch = useDispatch();

  const handleAssigneeChange = (taskId: number, assignee) => {
    dispatch(
      updateTaskSagaAction({
        projectId,
        taskId,
        data: {
          assignee,
        },
      }),
    );
  };

  const handleDueDateChange = (taskId, payload) => {
    dispatch(updateTaskSagaAction({ projectId, taskId, data: payload }));
  };

  const handleDelete = ({ taskId }: Task) =>
    dispatch(
      deleteTaskFromSpaceTabSagaAction({ projectId, spaceTabId, taskId }),
    );

  const handleAdd = (taskName: string) => {
    dispatch(
      createTaskFromSpaceTabSagaAction({
        projectId,
        spaceTabId,
        task: {
          taskName,
        },
      }),
    );

    return Promise.resolve();
  };

  const openTaskPane = (task: TaskId) => {
    navigateToTask(task);
  };

  const statusField = useStatusField();

  const handleStatusChange = (
    taskId: number,
    selected: FieldOption<StatusGroup>,
  ) => {
    if (statusField) {
      dispatch(
        updateTaskSagaAction({
          projectId,
          taskId,
          data: {
            fields: [
              {
                fieldId: statusField.fieldId,
                fieldValue: selected.value,
              },
            ],
          },
        }),
      );
    }
  };

  const taskListRef = useRef<HTMLDivElement | null>(null);

  const projectMembersQuery = useQuery(
    'project-members',
    () => {
      return ProjectAPI.getMembers(projectId);
    },
    { staleTime: GENERAL_API_STALE },
  );
  const projectMembers = projectMembersQuery.data;

  const { isLoading } = projectMembersQuery;

  return isLoading ? (
    <LoadingWrapper>
      <Loading />
    </LoadingWrapper>
  ) : (
    <SquadContext.Provider value={projectMembers || DEFAULT_SQUAD}>
      <TaskListWrapper ref={taskListRef}>
        <TaskList
          size="small"
          onItemClick={openTaskPane}
          refTo={taskListRef}
          data={tasks}
          handleStatusChange={handleStatusChange}
          handleAssigneeChange={handleAssigneeChange}
          handleDueDateChange={handleDueDateChange}
          handleDelete={handleDelete}
          handleNewTaskAdded={handleAdd}
        />
      </TaskListWrapper>
    </SquadContext.Provider>
  );
};
